import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym } from '../utils/teamAcronyms';

export default function LeagueTableCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate } = useDashboard();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const selectedRowRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiFetch(`/league-table?team=${encodeURIComponent(selectedTeam)}&date=${selectedDate}`, token)
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate]);

  const scrollToSelected = useCallback(() => {
    if (selectedRowRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const row = selectedRowRef.current;
      const containerHeight = container.clientHeight;
      const rowTop = row.offsetTop - container.offsetTop;
      const rowHeight = row.offsetHeight;
      container.scrollTop = rowTop - (containerHeight / 2) + (rowHeight / 2);
    }
  }, []);

  useEffect(() => {
    if (!loading && rows.length > 0) {
      requestAnimationFrame(scrollToSelected);
    }
  }, [loading, rows, scrollToSelected]);

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center">
        <strong>League Table</strong>
      </div>
      <div className="card-body p-0">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="text-center py-4 text-muted small">No data available</div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div ref={scrollContainerRef} className="table-responsive" style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <table className="table table-sm table-striped mb-0 small">
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
                <tr>
                  <th className="ps-3">#</th>
                  <th>Team</th>
                  <th className="text-center">P</th>
                  <th className="text-center">Pts</th>
                  <th className="text-center">GD</th>
                  <th className="text-center">Rating</th>
                  <th className="text-center pe-3">xPts</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.team}
                    ref={row.selected ? selectedRowRef : undefined}
                    className={row.selected ? 'table-primary' : ''}
                  >
                    <td className="ps-3">{row.rank}</td>
                    <td className={row.selected ? 'fw-bold' : ''}>{teamAcronym(row.team)}</td>
                    <td className="text-center">{row.played}</td>
                    <td className="text-center">{row.points}</td>
                    <td className="text-center" style={{ color: row.goalDifference > 0 ? '#198754' : row.goalDifference < 0 ? '#dc3545' : undefined }}>{row.goalDifference > 0 ? `+${row.goalDifference}` : row.goalDifference}</td>
                    <td className="text-center">{row.ppgRating.toFixed(2)}</td>
                    <td className="text-center pe-3">{row.expectedSeasonPoints.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
